import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { useNavigationBar } from "../../../context/NavigationContext";
import { SKStoryArtifact, ContentState, StoryArtifactItems } from "../../../models/SKArtifact";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import VideoPlayer from "../../../components/VideoPlayer";
import { FolderName, SKMediaType } from "../../../api/shared/uploadService";
import MultiTypeInVisibleInput from "./MultiTypeInVisibleInput";
import MultiTypeInput from "./MultiTypeInput";
import { isEditable } from "@testing-library/user-event/dist/utils";

export default function AddStory() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const navigate = useNavigate();
  const [editable, setEditable] = useState<boolean>(true);
  const [isValidForm, setIsValidForm] = useState(false);
  const [storyData, setStoryData] = useState<StoryArtifactItems[]>([]);
  const [story, setStory] = useState<StoryArtifactItems>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string>("");
  const charAllowed = 150;

  useEffect(() => {
    if (contentData.status === ContentState.DRAFT) {
      setEditable(true);
    } else {
      setEditable(false);
    }
  }, [contentData]);

  const schema = Yup.object().shape({
    storyItems: Yup.array()
      .of(
        Yup.object().shape({
          storyMediaType: Yup.mixed<SKMediaType>().required(),
          storyItemURL: Yup.string().url().required(),
          caption: Yup.string().required(),
        })
      )
      .required()
      .min(3, "storyItems must contain at least 3 items")
      .max(30, "storyItems must not contain more then 30 items"),
  });

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate();
  }, [contentData]);

  /*   useEffect(() => {
    setNavProps((prev) => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      enableLink: contentData.status === State.review ? false : isValidForm,
      onNext: submit,
    }));
  }, [setNavProps]); */

  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Story",
      getActionItems: undefined,
      enableLink: isEditable ? isValidForm : false,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
    if ((contentData as SKStoryArtifact).storyItems != null) {
      const enableLink = (contentData as SKStoryArtifact).storyItems.length === 3 && (contentData as SKStoryArtifact).storyItems.length > 0;
    }
  }, [setNavProps, (contentData as SKStoryArtifact).storyItems, isValidForm, isEditable]);

  useEffect(() => {
    if ((contentData as SKStoryArtifact).storyItems != null) {
      setStoryData((contentData as SKStoryArtifact).storyItems);
    }
  }, []);

  /*   useEffect(() => {
    setLetterData(contentData as SKLetterArtifact);
  }, [contentData]); */

  const submit = async () => {
    await saveData();
    window.history.back();
  };

  function handleImageChanges(index: number, fileUrl: string, fileType: SKMediaType): void {
    try {
      const newItem: StoryArtifactItems = {
        storyMediaType: fileType,
        storyItemURL: fileUrl,
      };
      if (newItem) {
        setStoryData(prev => {
          const updatedItems = prev.map((item, i) => (i === index ? newItem : item));
          const filteredList = updatedItems.filter(item => item.storyItemURL !== undefined);

          updateContentData({ storyItems: filteredList });
          return updatedItems;
        });
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }

  function onStart(index: number): void {
    try {
      if (index > storyData.length - 1) {
        const newItem: StoryArtifactItems = {
          storyMediaType: SKMediaType.none,
          storyItemURL: undefined,
          caption: "",
        };
        setStoryData(prev => {
          const updatedItems = [...prev, newItem];
          updateContentData({ storyItems: updatedItems });
          return updatedItems;
        });
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }

  // const handleFileChange = useCallback((index: number, fileUrl: string | null) => {
  //   console.log(`Upload successful for index ${index} File URL: ${fileUrl}`);
  //   setPhotoURLs(prevPhotoURLs => {
  //     const updatedPhotoURLs = [...prevPhotoURLs];
  //     updatedPhotoURLs[index] = fileUrl;
  //     return updatedPhotoURLs;
  //   });
  // }, []);

  const removeImage = (index: number) => {
    // const updatedItems = storyData.filter(item => item.storyID !== itemID);
    setStoryData(prev => {
      const items = prev.filter((_, i) => i !== index);
      updateContentData({ storyItems: items });
      return items;
    });
    // setStoryData(updatedItems);
  };

  const getUIElementCount = (count: number): number => {
    if (count === 0) return 3;
    if (count === 1) return 2;
    if (count === 2) return 1;
    if (count > 2 && count <= 30) return 1;
    return 0; // Default fallback
  };

  const uiElementCount = getUIElementCount(storyData.filter(item => item.storyItemURL !== undefined).length);

  const handleCaptionChange = (index: number, caption: string) => {
    // setStoryData(prev => {
    const updatedItems = [...storyData];
    updatedItems[index].caption = caption;
    updateContentData({ storyItems: updatedItems });
    // return updatedItems;
    // });
  };

  return (
    <>
      <div className="flex flex-col gap-5 mt-[12px]">
        <div className="h-screen overflow-auto">
          <div>
            <div className="flex flex-col">
              <div className="flex items-center">
                <span>
                  <span className="content_count">1</span>
                </span>
                <span className="content_title">Add Media</span>
              </div>
              <div className="notes-class-content">
                Minimum 3 and Maximum 30 allowed. Recommended aspect ratio is either 4:3, 1:1 or 3:4. JPG, PNG, HEIF, or GIF for photos, and MP4, MOV,
                or M4V for videos.
              </div>
            </div>
            <div className="flex flex-col gap-[30px] max-w-[300px]">
              {storyData
                .filter(item => item.storyItemURL !== undefined)
                .map((item, index) => (
                  <div key={index} className="bg-white p-0.5 rounded-lg h-max">
                    {item.storyMediaType === SKMediaType.photo ? (
                      <div className="multimedia-input">
                        {isEditable && (
                          <button
                            className="absolute top-2 right-2"
                            onClick={e => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                          >
                            <img className="w-[40px] h-[40px]" src="/icons/CloseButton.png" alt="remove" />
                          </button>
                        )}

                        <img src={item.storyItemURL!} alt={`photo_${index}`} className="object-cover w-full h-full" />
                      </div>
                    ) : (
                      <div className="multimedia-input">
                        {isEditable && (
                          <button
                            className="absolute z-10 top-2 right-2" // Added z-10 to ensure it is on top
                            onClick={e => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                          >
                            <img className="w-[40px] h-[40px]" src="/icons/CloseButton.png" alt="remove" />
                          </button>
                        )}

                        <VideoPlayer videoUrl={item.storyItemURL!} />
                      </div>
                    )}
                    <textarea
                      placeholder="Enter caption..."
                      value={item.caption || ""}
                      onChange={e => handleCaptionChange(index, e.target.value)}
                      className="w-full p-2 text-sm border border-gray-300 rounded-lg"
                      rows={3}
                      disabled={!editable}
                    />

                    {error && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{error}</span>}
                    {!error && <div className="remaining-characters">{charAllowed - (item.caption?.length ?? 0)} characters remaining.</div>}
                  </div>
                ))}
              <>
                {isEditable &&
                  [...Array(uiElementCount)].map((_, index) => (
                    <div id="img_2" className="multimedia-input">
                      <div className="mb-4">
                        <MultiTypeInput
                          docId={artifactId!}
                          maxFileSize={5}
                          imageUrl={storyData[1]?.storyItemURL || ""}
                          // caption={storyData[1]?.caption || ""}
                          // mediaType={storyData[1]?.storyMediaType || ""}
                          onFileChange={handleImageChanges}
                          onStart={onStart}
                          index={storyData.length}
                          editable={isEditable}
                          folder={FolderName.content}
                        />
                      </div>
                    </div>
                  ))}
              </>
              {/* {storyData.length >= 0 && storyData.length <= 2 && (
                <>
                  {storyData.length === 0 && (
                    <div id="img_2" className="multimedia-input">
                      <div className="mb-4">
                        <MultiTypeInput
                          docId={artifactId!}
                          maxFileSize={5}
                          imageUrl={storyData[1]?.storyItemUrl || ""}
                          onFileChange={handleImageChanges}
                          index={1}
                          editable={isEditable}
                          folder={FolderName.list}
                        />
                      </div>
                    </div>
                  )}
                  {storyData.length < 3 && (
                    <div className="multimedia-input">
                      <div id="img_1" className="mb-4">
                        <MultiTypeInput
                          docId={artifactId!}
                          maxFileSize={5}
                          imageUrl={""}
                          onFileChange={handleImageChanges}
                          index={storyData.length + 1}
                          editable={isEditable}
                          folder={FolderName.list}
                        />
                      </div>
                    </div>
                  )}
                  {storyData.length < 2 && (
                    <div className="multimedia-input">
                      <div id="img_1" className="mb-4">
                        <MultiTypeInput
                          docId={artifactId!}
                          maxFileSize={5}
                          imageUrl={""}
                          onFileChange={handleImageChanges}
                          index={storyData.length + 1}
                          editable={isEditable}
                          folder={FolderName.list}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {storyData.length >= 3 && (
                <div className="multimedia-input">
                  <div id="img_4" className="mb-4">
                    <MultiTypeInput
                      docId={artifactId!}
                      maxFileSize={5}
                      imageUrl={""}
                      onFileChange={handleImageChanges}
                      index={storyData.length + 1}
                      editable={isEditable}
                      folder={FolderName.list}
                    />
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
